import OlMap from '@m1/ol-map';
import useUpdateEffect from '@restart/hooks/useUpdateEffect';
import classNames from 'classnames/bind';
import { toLonLat } from 'ol/proj';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { RiArrowRightUpLine } from 'react-icons/ri';
import { v4 as uuidv4 } from 'uuid';

import styles from './common.module.scss';

import { FieldNumber } from '@/components/ui/kit/Field';
import LabelGroup from '@/components/ui/LabelGroup';
import { MAVLINK_MESSAGE_IDS as MESSAGE } from '@/define/messageId';
import { MODE } from '@/define/robotStatus';
import useActionCommand from '@/hooks/command/mavlink/useActionCommand';
import { NotifierService as notifier } from '@/libs/Notifier';
import telemetryStore from '@/stores/telemetryStore';
import { executeMavLinkCommand } from '@/utils/CommandUtils';

const cx = classNames.bind(styles);

const Panel = ({ data: robots }) => {
  const [isProcessing, setProcessing] = useState(false);
  const [targetLocation, setTargetLocation] = useState(null);
  const inputRefs = useRef({
    altitude: useRef(),
    speed: useRef(),
  }).current;
  const actionCommand = useActionCommand();
  const map = OlMap.getMap();

  const isShowAltitude = useMemo(() => {
    return ['Copter', 'Plane'].includes(robots[0].model.vehicle);
  }, [robots]);

  useEffect(() => {
    map.getTargetElement().style.cursor = 'crosshair';
    map.on('click', clickOnMap);

    return () => {
      map.getTargetElement().style.cursor = 'default';
      map.un('click', clickOnMap);
    };
  }, [robots]);

  //#region Process Functions
  const process = (robot) => {
    switch (robot.model.maker) {
      case 'ArduPilot':
        switch (robot.model.vehicle) {
          case 'Copter':
            return processArduCopter(robot);
          case 'Plane':
            return processArduPlane(robot);
          default:
            return Promise.resolve();
        }
      case 'PX4':
        return processPX4Rover(robot);

      default:
        return Promise.resolve();
    }
  };

  const processArduCopter = async (robot) => {
    const { lat, lng } = targetLocation;
    const { alt, ralt } = telemetryStore.get(robot.id, MESSAGE.GLOBAL_POSITION_INT);
    const altitude = Number(inputRefs.altitude.current.value);
    const speed = Number(inputRefs.speed.current.value);

    try {
      await executeMavLinkCommand(robot, actionCommand.setMode(MODE.ARDUPILOT.COPTER.GUIDED.id));
      await executeMavLinkCommand(robot, actionCommand.changeSpeed(1, speed));
      await executeMavLinkCommand(robot, actionCommand.goto(-1, NaN, lat, lng, alt - ralt + altitude));

      onSuccess(robot);
    } catch (error) {
      onFailure(robot);
    }
  };

  const processArduPlane = async (robot) => {
    const { lat, lng } = targetLocation;
    const { alt, ralt } = telemetryStore.get(robot.id, MESSAGE.GLOBAL_POSITION_INT);
    const altitude = Number(inputRefs.altitude.current.value);
    const speed = Number(inputRefs.speed.current.value);

    try {
      await executeMavLinkCommand(robot, actionCommand.setMode(MODE.ARDUPILOT.PLANE.GUIDED.id));
      await executeMavLinkCommand(robot, actionCommand.changeSpeed(0, speed));
      await executeMavLinkCommand(robot, actionCommand.goto(-1, 0, lat, lng, alt - ralt + altitude));

      onSuccess(robot);
    } catch (error) {
      onFailure(robot);
    }
  };

  const processPX4Rover = async (robot) => {
    const { lat, lng } = targetLocation;
    const { alt } = telemetryStore.get(robot.id, MESSAGE.GLOBAL_POSITION_INT);
    const speed = Number(inputRefs.speed.current.value);

    try {
      await executeMavLinkCommand(robot, actionCommand.goto(speed, NaN, lat, lng, alt));

      onSuccess(robot);
    } catch (error) {
      onFailure(robot);
    }
  };
  //#endregion

  //#region Result Handlers
  const onSuccess = (robot) => {
    OlMap.goto.addMarker(robot.id, targetLocation, robot.color);

    notifier.success(`${robot.name} is moving to the target location.`);
    setProcessing(false);
  };

  const onFailure = (robot) => {
    notifier.error(`Failed to move ${robot.name} to the target location.`);
    setProcessing(false);
  };
  //#endregion

  useUpdateEffect(() => {
    if (!isProcessing) return;

    process(robots[0]);
  }, [isProcessing]);

  const clickOnMap = (e) => {
    const [lng, lat] = toLonLat(e.coordinate);

    setTargetLocation({ lat, lng });
    setProcessing(true);
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;

    inputRefs[name].current.value = Number(value).toFixed(1);
  };

  return (
    <div className={cx('container')}>
      <div className={cx('form')}>
        {isShowAltitude && (
          <LabelGroup htmlFor="altitude" label="Altitude">
            <div className={cx('fieldWrapper')}>
              <FieldNumber
                id="altitude"
                ref={inputRefs.altitude}
                name="altitude"
                defaultValue={50}
                onBlur={handleBlur}
              />
              <div className={cx('unit')}>m</div>
            </div>
          </LabelGroup>
        )}
        <LabelGroup htmlFor="speed" label="Speed">
          <div className={cx('fieldWrapper')}>
            <FieldNumber id="speed" ref={inputRefs.speed} name="speed" defaultValue={10} onBlur={handleBlur} />
            <div className={cx('unit')}>m/s</div>
          </div>
        </LabelGroup>
      </div>
      <div className={cx('desc')}>Click on a location on the map where you want to move</div>
    </div>
  );
};

const Command = {};
Command.id = uuidv4();
Command.name = 'Goto';
Command.displayMode = 'sidebar';
Command.singleRobotOnly = true;
Command.commanderOnly = true;
Command.Icon = RiArrowRightUpLine;
Command.Panel = Panel;

export default Command;
